/**
 * What the device keeps of its account (part 36a, principle C3, table AC): the account logged in to (its ID, address,
 * newest trial copy, whether the relay signed it out, the purge date last told), the highest KDF parameters seen per
 * address (C7), the latest time seen (C2) and the writer's "not now" (AC3); from part 36c the newest notice of a new
 * device seen here (NO2) and each account's pending deletion as this device saw it (table FD). No secret: no password,
 * login secret or session token. Read at the boundary field by field: what doesn't read takes its default, so a damaged record signs the
 * device out rather than letting a wrong value through; the highest parameters are read one address at a time, so one
 * damaged entry costs only its own.
 */
import { readKdfRecord } from '@cockatrice/crypto';
import type { KdfParameters } from '@cockatrice/crypto';
import { canonicalEmail } from './email.ts';
import type { Email } from './email.ts';
import { fieldsOf, isAccountId, signedCopyOf } from './wire.ts';
import type { AccountId, SignedCopy } from './wire.ts';

/** The account this device is logged in to, or was until the relay ended its session (C8). */
export interface StoredAccount {
  readonly id: AccountId;
  readonly email: Email;
  readonly copy: SignedCopy | null;
  /** C8, AC7: the relay ended this device's session; the copy still decides writing. */
  readonly signedOut: boolean;
  /** The purge date the relay last told (part 35a, UP8), shown while read-only; null for none. */
  readonly purgeAt: number | null;
  /** Part 38a: when its own shares and links end, for an account that began as a guest (an older record: null). */
  readonly sharesEndAt: number | null;
  /** Part 36c, NO2: the newest notice the writer has seen on this device (0: none yet); null before this device first looked. */
  readonly seenNotice: number | null;
}

/** FD1, FD2: a deletion of an account as this device saw it pending: its due date, and when this device first saw that date. */
export interface DeletionMark {
  readonly dueAt: number;
  readonly seenAt: number;
}

/** The device's account record. */
export interface AccountRecord {
  readonly account: StoredAccount | null;
  readonly highest: Readonly<Record<string, KdfParameters>>;
  readonly latestSeen: number;
  readonly notNow: boolean;
  /** FD: each account's pending deletion this device saw, kept across a logout (FD10). */
  readonly deleting: Readonly<Record<string, DeletionMark>>;
  /** Fix-next 84, table LXC: accounts this device logged out of whose relay hasn't yet taken the logout (LXC1, LXC3). */
  readonly logoutOwed: readonly AccountId[];
}

/** LXC: the most logouts kept owed; past it the oldest goes (a device logged out of so many accounts offline is rare). */
export const MAX_OWED_LOGOUTS = 16;

/** A device that has kept nothing. */
export const NO_RECORD: AccountRecord = Object.freeze({
  account: null,
  highest: Object.freeze({}),
  latestSeen: 0,
  notNow: false,
  deleting: Object.freeze({}),
  logoutOwed: Object.freeze([]),
});

/** LXC1: `owed` with `account` owed too: once, newest last, at most {@link MAX_OWED_LOGOUTS}. */
export function owing(owed: readonly AccountId[], account: AccountId): readonly AccountId[] {
  return [...owed.filter((each) => each !== account), account].slice(-MAX_OWED_LOGOUTS);
}

/** LXC: `owed` without `account`. */
export function settled(owed: readonly AccountId[], account: AccountId): readonly AccountId[] {
  return owed.filter((each) => each !== account);
}

function timeOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/** Parameters as kept: read through 25a's record reader with a stand-in salt, so the bounds are its own. */
function parametersOf(value: unknown): KdfParameters | null {
  const fields = fieldsOf(value);
  if (fields === null || Object.keys(fields).length !== 2) return null;
  const record = readKdfRecord({ version: 1, salt: new Uint8Array(16), memoryKiB: fields['memoryKiB'], passes: fields['passes'] });
  return record === null ? null : { memoryKiB: record.memoryKiB, passes: record.passes };
}

function accountOf(value: unknown): StoredAccount | null {
  const fields = fieldsOf(value);
  const id = fields?.['id'];
  const email = typeof fields?.['email'] === 'string' ? canonicalEmail(fields['email']) : null;
  const given = fields?.['copy'];
  const copy = given === null ? null : signedCopyOf(given);
  const signedOut = fields?.['signedOut'];
  const purge = fields?.['purgeAt'];
  const purgeAt = purge === null ? null : timeOf(purge);
  const shares = fields?.['sharesEndAt'] ?? null;
  const sharesEndAt = shares === null ? null : timeOf(shares);
  // Part 36c: a record from before it has never looked at the notices.
  const seen = fields?.['seenNotice'];
  const seenNotice = seen === undefined || seen === null ? null : (timeOf(seen) ?? undefined);
  if (!isAccountId(id) || email === null || email !== fields?.['email'] || (given !== null && copy === null) || typeof signedOut !== 'boolean') return null;
  if ((shares !== null && sharesEndAt === null) || (purge !== null && purgeAt === null) || seenNotice === undefined) return null;
  return { id, email, copy, signedOut, purgeAt, sharesEndAt, seenNotice };
}

/** A deletion mark as kept, or null. */
function markOf(value: unknown): DeletionMark | null {
  const fields = fieldsOf(value);
  const dueAt = timeOf(fields?.['dueAt']);
  const seenAt = timeOf(fields?.['seenAt']);
  return dueAt === null || seenAt === null ? null : { dueAt, seenAt };
}

/** The record as kept, or its defaults where it doesn't read. */
export function readAccountRecord(raw: unknown): AccountRecord {
  const fields = fieldsOf(raw);
  if (fields === null) return NO_RECORD;
  const highest: Record<string, KdfParameters> = {};
  for (const [address, value] of Object.entries(fieldsOf(fields['highest']) ?? {})) {
    const parameters = parametersOf(value);
    if (parameters !== null && canonicalEmail(address) === address) highest[address] = parameters;
  }
  // FD: one damaged mark costs only its own account's (that device then keeps its books, FD5).
  const deleting: Record<string, DeletionMark> = {};
  for (const [account, value] of Object.entries(fieldsOf(fields['deleting']) ?? {})) {
    const mark = markOf(value);
    if (mark !== null && isAccountId(account)) deleting[account] = mark;
  }
  // LXC: an entry that isn't an account ID is dropped, each one kept once (an older record has none).
  const owed = Array.isArray(fields['logoutOwed']) ? fields['logoutOwed'] : [];
  const logoutOwed = owed.filter(isAccountId).reduce<readonly AccountId[]>(owing, []);
  return {
    account: accountOf(fields['account']),
    highest,
    latestSeen: timeOf(fields['latestSeen']) ?? 0,
    notNow: fields['notNow'] === true,
    deleting,
    logoutOwed,
  };
}
